import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Autocomplete,
  Chip,
  InputAdornment
} from '@mui/material';
import { LocationOn, MyLocation, Search } from '@mui/icons-material';
import { geocodingService, LocationSearchResult } from '../services/geocodingService';
import { OptionsManager, getAllActivityTypes, getActivityTypeDisplayName, getActivityTypeIcon } from '../utils/optionsManager';
import {
  feetToMeters,
  fahrenheitToCelsius,
  metersToFeet,
  celsiusToFahrenheit,
  psiToBar,
  barToPsi,
  calculateSAC,
  getTankVolume,
  getDiveTankVolume,
  getGasConsumptionDisplayValue,
  getBaseRmvFromDisplayValue,
  getGasConsumptionUnitLabel,
  getGasConsumptionMetricTitle,
  GasConsumptionMetric,
  PressureUnit
} from '../utils/unitConversions';

interface DiveEntryFormProps {
  open: boolean;
  onClose: () => void;
  /** Called after a save; receives the new dive's id when a dive was created (not on edit). */
  onSuccess: (createdDiveId?: string) => void;
  diveToEdit?: any; // Optional dive object for editing
  editMode?: boolean; // If true, we're editing an existing dive
}

interface DiveFormData {
  startTime: string;
  durationMinutes: string;
  maxDepth: string;
  location: string;
  latitude: string;
  longitude: string;
  avgDepth: string;
  waterTemperature: string;
  diveType: string;
  safetyStopMinutes: string;
  visibility: string;
  weightUsed: string;
  weightUnit: string;
  pressureUnit: string;
  tankSize: string;
  customTankVolume: string;
  startPressure: string;
  endPressure: string;
  gasMetric: GasConsumptionMetric;
  gasConsumption: string;
  suitType: string;
  suitThicknessMm: string;
}

// Decimal places for a gas consumption display value (matches formatGasConsumptionDisplayValue)
const gasDecimals = (metric: GasConsumptionMetric, pressureUnit: string): number =>
  metric === 'rmv' && pressureUnit === 'psi' ? 3 : 1;

const DiveEntryForm: React.FC<DiveEntryFormProps> = ({ open, onClose, onSuccess, diveToEdit, editMode = false }) => {
  // Load user preferences to determine default units
  const optionsManager = OptionsManager.getInstance();
  const unitPreferences = optionsManager.getOption('units');

  // Determine default units and labels based on user preferences
  const defaultWeightUnit = unitPreferences.weight; // 'kg' or 'lbs'
  const defaultPressureUnit = unitPreferences.pressure; // 'bar' or 'psi'
  const defaultGasMetric: GasConsumptionMetric = optionsManager.getOption('display').gasConsumptionMetric || 'rmv';
  const depthLabel = unitPreferences.depth === 'meters' ? 'meters' : 'feet';
  const tempLabel = unitPreferences.temperature === 'celsius' ? '°C' : '°F';
  const isMetric = unitPreferences.depth === 'meters';

  // Initialize form data - either from diveToEdit or with defaults
  const getInitialFormData = (): DiveFormData => {
    if (editMode && diveToEdit) {
      const durationMinutes = diveToEdit.duration ?
        (typeof diveToEdit.duration === 'string' ? parseFloat(diveToEdit.duration.split(':')[0]) * 60 + parseFloat(diveToEdit.duration.split(':')[1]) :
         Math.round((new Date(diveToEdit.endTime).getTime() - new Date(diveToEdit.startTime).getTime()) / 60000)) : '';

      // Backend stores depth in meters and temperature in Celsius
      // Convert to display units based on user preferences
      let maxDepthDisplay = diveToEdit.maxDepth || '';
      let avgDepthDisplay = diveToEdit.avgDepth || '';
      let waterTempDisplay = diveToEdit.waterTemperature || '';

      // Convert depth from meters to feet if user preference is imperial
      if (unitPreferences.depth === 'feet' && diveToEdit.maxDepth) {
        maxDepthDisplay = metersToFeet(diveToEdit.maxDepth).toFixed(1);
      } else if (diveToEdit.maxDepth) {
        maxDepthDisplay = diveToEdit.maxDepth.toString();
      }

      if (unitPreferences.depth === 'feet' && diveToEdit.avgDepth) {
        avgDepthDisplay = metersToFeet(diveToEdit.avgDepth).toFixed(1);
      } else if (diveToEdit.avgDepth) {
        avgDepthDisplay = diveToEdit.avgDepth.toString();
      }

      // Convert temperature from Celsius to Fahrenheit if user preference is imperial
      if (unitPreferences.temperature === 'fahrenheit' && diveToEdit.waterTemperature) {
        waterTempDisplay = celsiusToFahrenheit(diveToEdit.waterTemperature).toFixed(1);
      } else if (diveToEdit.waterTemperature) {
        waterTempDisplay = diveToEdit.waterTemperature.toString();
      }

      return {
        startTime: diveToEdit.startTime ? new Date(diveToEdit.startTime).toISOString().slice(0, 16) : '',
        durationMinutes: durationMinutes.toString(),
        maxDepth: maxDepthDisplay.toString(),
        location: diveToEdit.location || '',
        latitude: diveToEdit.latitude?.toString() || '',
        longitude: diveToEdit.longitude?.toString() || '',
        avgDepth: avgDepthDisplay.toString(),
        waterTemperature: waterTempDisplay.toString(),
        diveType: diveToEdit.diveType || 'Scubadiving',
        safetyStopMinutes: diveToEdit.stats?.safetyStopTime?.toString() || '3',
        visibility: diveToEdit.stats?.visibility || 'Good',
        weightUsed: diveToEdit.additionalData?.weightUsed?.toString() || '',
        weightUnit: diveToEdit.additionalData?.weightUnit || defaultWeightUnit,
        pressureUnit: diveToEdit.additionalData?.pressureUnit || defaultPressureUnit,
        tankSize: diveToEdit.additionalData?.tankSize || optionsManager.getOption('equipment').tankSize,
        customTankVolume: diveToEdit.additionalData?.customTankVolume?.toString() || optionsManager.getOption('equipment').customTankVolume?.toString() || '',
        startPressure: diveToEdit.additionalData?.startPressure?.toString() || '',
        endPressure: diveToEdit.additionalData?.endPressure?.toString() || '',
        gasMetric: defaultGasMetric,
        gasConsumption: (() => {
          const manualRmv = diveToEdit.additionalData?.manualRmv;
          if (!manualRmv || manualRmv <= 0) return '';
          const pressureUnit = (diveToEdit.additionalData?.pressureUnit || defaultPressureUnit) as PressureUnit;
          const display = getGasConsumptionDisplayValue(
            manualRmv,
            defaultGasMetric,
            pressureUnit,
            getDiveTankVolume(diveToEdit) ?? undefined
          );
          return display !== null ? display.toFixed(gasDecimals(defaultGasMetric, pressureUnit)) : '';
        })(),
        suitType: diveToEdit.additionalData?.suit?.type || 'wetsuit',
        suitThicknessMm: diveToEdit.additionalData?.suit?.thicknessMm?.toString() || ''
      };
    }

    return {
      startTime: new Date().toISOString().slice(0, 16),
      durationMinutes: '',
      maxDepth: '',
      location: '',
      latitude: '',
      longitude: '',
      avgDepth: '',
      waterTemperature: '',
      diveType: 'Scubadiving',
      safetyStopMinutes: '3',
      visibility: 'Good',
      weightUsed: '',
      weightUnit: defaultWeightUnit,
      pressureUnit: defaultPressureUnit,
      tankSize: optionsManager.getOption('equipment').tankSize,
      customTankVolume: optionsManager.getOption('equipment').customTankVolume?.toString() || '',
      startPressure: '',
      endPressure: '',
      gasMetric: defaultGasMetric,
      gasConsumption: '',
      suitType: 'wetsuit',
      suitThicknessMm: ''
    };
  };

  const [formData, setFormData] = useState<DiveFormData>(getInitialFormData());

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationSearchResults, setLocationSearchResults] = useState<LocationSearchResult[]>([]);
  const [locationSearchLoading, setLocationSearchLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<LocationSearchResult | null>(null);
  const [showManualCoords, setShowManualCoords] = useState(false);
  const [gettingCurrentLocation, setGettingCurrentLocation] = useState(false);
  const [locationInputValue, setLocationInputValue] = useState('');

  // Debounce timer ref for location search
  const searchDebounceRef = useRef<NodeJS.Timeout>();

  // Update form defaults when preferences change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      weightUnit: defaultWeightUnit,
      pressureUnit: defaultPressureUnit
    }));
  }, [defaultWeightUnit, defaultPressureUnit]);

  // Repopulate form when dive changes or dialog opens
  useEffect(() => {
    if (open) {
      setFormData(getInitialFormData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, diveToEdit, editMode]);

  // Tank volume in liters for the currently selected tank
  const formTankVolume = formData.tankSize === 'custom'
    ? (() => {
        const volume = parseFloat(formData.customTankVolume);
        return Number.isFinite(volume) && volume > 0 ? volume : null;
      })()
    : getTankVolume(formData.tankSize);

  // RMV (L/min) derived from tank pressures when every input it needs is present
  const calculatedRmv = (() => {
    if (!formTankVolume) return null;

    const start = parseFloat(formData.startPressure);
    const end = parseFloat(formData.endPressure);
    const duration = parseFloat(formData.durationMinutes);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start <= end) return null;
    if (!Number.isFinite(duration) || duration <= 0) return null;

    const avgDepthInput = parseFloat(formData.avgDepth);
    const maxDepthInput = parseFloat(formData.maxDepth);
    let avgDepth = Number.isFinite(avgDepthInput) && avgDepthInput > 0
      ? avgDepthInput
      : Number.isFinite(maxDepthInput) && maxDepthInput > 0
        ? maxDepthInput * 0.7 // matches the backend's average depth estimate
        : null;
    if (avgDepth === null) return null;
    if (unitPreferences.depth === 'feet') avgDepth = feetToMeters(avgDepth);

    const startBar = formData.pressureUnit === 'psi' ? psiToBar(start) : start;
    const endBar = formData.pressureUnit === 'psi' ? psiToBar(end) : end;
    return calculateSAC(formTankVolume, startBar, endBar, duration, avgDepth);
  })();

  const gasAutoCalculated = calculatedRmv !== null;

  const calculatedGasValue = (() => {
    if (calculatedRmv === null) return null;
    const display = getGasConsumptionDisplayValue(
      calculatedRmv,
      formData.gasMetric,
      formData.pressureUnit as PressureUnit,
      formTankVolume ?? undefined
    );
    return display !== null ? display.toFixed(gasDecimals(formData.gasMetric, formData.pressureUnit)) : null;
  })();

  // A pressure-derived value always wins over a directly entered one
  useEffect(() => {
    if (calculatedGasValue === null) return;
    setFormData(prev => (prev.gasConsumption === calculatedGasValue ? prev : { ...prev, gasConsumption: calculatedGasValue }));
  }, [calculatedGasValue]);

  const handleChange = (field: keyof DiveFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  // Pasting a "lat, long" pair (e.g. copied from a dive detail page) into either
  // coordinate field fills both. Single-value pastes fall through untouched.
  const handleCoordinatePairPaste = (event: React.ClipboardEvent) => {
    const text = event.clipboardData.getData('text').trim();
    const match = text.match(/^\(?\s*(-?\d{1,3}(?:\.\d+)?)\s*(?:[,;]\s*|\s+)(-?\d{1,3}(?:\.\d+)?)\s*\)?$/);
    if (!match) return;
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (Number.isNaN(lat) || Number.isNaN(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return;
    event.preventDefault();
    setFormData(prev => ({ ...prev, latitude: match[1], longitude: match[2] }));
  };

  const handlePressureUnitChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextUnit = event.target.value;
    const previousUnit = formData.pressureUnit;

    const convertPressure = (value: string) => {
      if (!value || previousUnit === nextUnit) {
        return value;
      }

      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) {
        return value;
      }

      const converted = previousUnit === 'bar' ? barToPsi(parsed) : psiToBar(parsed);
      return nextUnit === 'psi' ? converted.toFixed(0) : converted.toFixed(1);
    };

    // Re-express the gas consumption value in the new unit (L/min <-> ft³/min, bar/min <-> psi/min)
    const convertGasConsumption = (value: string) => {
      if (!value || previousUnit === nextUnit) {
        return value;
      }

      const parsed = parseFloat(value);
      if (Number.isNaN(parsed)) {
        return value;
      }

      const baseRmv = getBaseRmvFromDisplayValue(parsed, formData.gasMetric, previousUnit as PressureUnit, formTankVolume ?? undefined);
      if (baseRmv === null) {
        return value;
      }

      const display = getGasConsumptionDisplayValue(baseRmv, formData.gasMetric, nextUnit as PressureUnit, formTankVolume ?? undefined);
      return display !== null ? display.toFixed(gasDecimals(formData.gasMetric, nextUnit)) : value;
    };

    setFormData({
      ...formData,
      pressureUnit: nextUnit,
      startPressure: convertPressure(formData.startPressure),
      endPressure: convertPressure(formData.endPressure),
      gasConsumption: convertGasConsumption(formData.gasConsumption),
    });
  };

  const handleGasMetricChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextMetric = event.target.value as GasConsumptionMetric;
    const previousMetric = formData.gasMetric;
    const pressureUnit = formData.pressureUnit as PressureUnit;

    // Re-express the current value in the new metric where possible
    let converted = formData.gasConsumption;
    const parsed = parseFloat(formData.gasConsumption);
    if (!Number.isNaN(parsed) && previousMetric !== nextMetric) {
      const baseRmv = getBaseRmvFromDisplayValue(parsed, previousMetric, pressureUnit, formTankVolume ?? undefined);
      const display = baseRmv !== null
        ? getGasConsumptionDisplayValue(baseRmv, nextMetric, pressureUnit, formTankVolume ?? undefined)
        : null;
      converted = display !== null ? display.toFixed(gasDecimals(nextMetric, pressureUnit)) : '';
    }

    setFormData({
      ...formData,
      gasMetric: nextMetric,
      gasConsumption: converted,
    });
  };

  // Debounced location search for better performance
  const handleLocationSearch = useCallback((query: string) => {
    // Clear previous timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!query || query.length < 3) {
      setLocationSearchResults([]);
      setLocationSearchLoading(false);
      return;
    }

    setLocationSearchLoading(true);

    // Debounce search by 500ms
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const results = await geocodingService.searchLocations(query);
        setLocationSearchResults(results);
      } catch (error) {
        console.error('Location search failed:', error);
        setLocationSearchResults([]);
      } finally {
        setLocationSearchLoading(false);
      }
    }, 500);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const handleLocationSelect = (location: LocationSearchResult | null) => {
    setSelectedLocation(location);
    if (location) {
      setFormData({
        ...formData,
        location: location.displayName,
        latitude: location.latitude.toString(),
        longitude: location.longitude.toString()
      });
    }
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      return;
    }

    setGettingCurrentLocation(true);
    setError(null);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;

        try {
          // Get location name from coordinates
          const locationName = await geocodingService.getLocationName(lat, lng);

          setFormData({
            ...formData,
            location: locationName,
            latitude: lat.toString(),
            longitude: lng.toString()
          });

          setSelectedLocation({
            displayName: locationName,
            latitude: lat,
            longitude: lng,
            type: 'current_location',
            address: {}
          });

        } catch (error) {
          console.error('Failed to get location name:', error);
          setFormData({
            ...formData,
            latitude: lat.toString(),
            longitude: lng.toString()
          });
        } finally {
          setGettingCurrentLocation(false);
        }
      },
      (error) => {
        console.error('Geolocation error:', error);
        setError('Failed to get your current location. Please check your browser permissions.');
        setGettingCurrentLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.startTime) {
      setError('Start time is required');
      return;
    }
    if (!formData.durationMinutes || parseFloat(formData.durationMinutes) <= 0) {
      setError('Duration must be greater than 0');
      return;
    }
    if (!formData.maxDepth || parseFloat(formData.maxDepth) <= 0) {
      setError('Max depth must be greater than 0');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Dynamically import the service to avoid circular dependencies
      const { diveService } = await import('../services/diveService');

      // Convert depth and temperature to metric if user is in imperial mode
      // API expects depth in meters and temperature in Celsius
      let maxDepthMeters = parseFloat(formData.maxDepth);
      let avgDepthMeters = formData.avgDepth ? parseFloat(formData.avgDepth) : undefined;
      let waterTempCelsius = formData.waterTemperature ? parseFloat(formData.waterTemperature) : undefined;

      // Convert depth if user entered in feet
      if (unitPreferences.depth === 'feet') {
        maxDepthMeters = feetToMeters(maxDepthMeters);
        if (avgDepthMeters !== undefined) {
          avgDepthMeters = feetToMeters(avgDepthMeters);
        }
      }

      // Convert temperature if user entered in Fahrenheit
      if (unitPreferences.temperature === 'fahrenheit' && waterTempCelsius !== undefined) {
        waterTempCelsius = fahrenheitToCelsius(waterTempCelsius);
      }

      const diveData = {
        startTime: new Date(formData.startTime).toISOString(),
        durationMinutes: parseFloat(formData.durationMinutes),
        maxDepth: maxDepthMeters,
        location: formData.location || undefined,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        avgDepth: avgDepthMeters,
        waterTemperature: waterTempCelsius,
        diveType: formData.diveType,
        safetyStopMinutes: formData.safetyStopMinutes ? parseInt(formData.safetyStopMinutes) : undefined,
        visibility: formData.visibility || undefined,
        weightUsed: formData.weightUsed ? parseFloat(formData.weightUsed) : undefined,
        weightUnit: formData.weightUnit,
        airConsumed:
          formData.startPressure &&
          formData.endPressure &&
          parseFloat(formData.startPressure) > parseFloat(formData.endPressure)
            ? parseFloat(formData.startPressure) - parseFloat(formData.endPressure)
            : undefined,
        pressureUnit: formData.pressureUnit,
        tankSize: formData.tankSize,
        customTankVolume: formData.tankSize === 'custom' && formData.customTankVolume
          ? parseFloat(formData.customTankVolume)
          : undefined,
        startPressure: formData.startPressure ? parseFloat(formData.startPressure) : undefined,
        endPressure: formData.endPressure ? parseFloat(formData.endPressure) : undefined,
        manualRmv: (() => {
          // Only persist a directly entered value; a pressure-derived one is recalculated from the pressures
          if (gasAutoCalculated) return undefined;
          const entered = parseFloat(formData.gasConsumption);
          if (!Number.isFinite(entered) || entered <= 0) return undefined;
          return getBaseRmvFromDisplayValue(
            entered,
            formData.gasMetric,
            formData.pressureUnit as PressureUnit,
            formTankVolume ?? undefined
          ) ?? undefined;
        })(),
        suitType: formData.suitType,
        suitThicknessMm: formData.suitThicknessMm ? parseFloat(formData.suitThicknessMm) : undefined
      };

      // Use update or create based on editMode
      let createdDiveId: string | undefined;
      if (editMode && diveToEdit?.id) {
        await diveService.updateDive(diveToEdit.id, diveData);
      } else {
        const created = await diveService.createDive(diveData);
        createdDiveId = created?.dive?.id;
      }

      // Reset form
      setFormData({
        startTime: new Date().toISOString().slice(0, 16),
        durationMinutes: '',
        maxDepth: '',
        location: '',
        latitude: '',
        longitude: '',
        avgDepth: '',
        waterTemperature: '',
        diveType: 'Scubadiving',
        safetyStopMinutes: '3',
        visibility: 'Good',
        weightUsed: '',
        weightUnit: defaultWeightUnit,
        pressureUnit: defaultPressureUnit,
        tankSize: optionsManager.getOption('equipment').tankSize,
        customTankVolume: optionsManager.getOption('equipment').customTankVolume?.toString() || '',
        startPressure: '',
        endPressure: '',
        gasMetric: defaultGasMetric,
        gasConsumption: '',
        suitType: 'wetsuit',
        suitThicknessMm: ''
      });
      setSelectedLocation(null);
      setLocationSearchResults([]);
      setLocationInputValue('');
      setShowManualCoords(false);
      setGettingCurrentLocation(false);

      onSuccess(createdDiveId);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create dive. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
      setSelectedLocation(null);
      setLocationSearchResults([]);
      setLocationInputValue('');
      setShowManualCoords(false);
      setGettingCurrentLocation(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{editMode ? 'Edit Dive' : 'Log a New Dive'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Required Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date & Time"
                type="datetime-local"
                value={formData.startTime}
                onChange={handleChange('startTime')}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Duration (minutes)"
                type="number"
                value={formData.durationMinutes}
                onChange={handleChange('durationMinutes')}
                fullWidth
                required
                inputProps={{ min: 1, step: 1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={`Max Depth (${depthLabel})`}
                type="number"
                value={formData.maxDepth}
                onChange={handleChange('maxDepth')}
                fullWidth
                required
                inputProps={{ min: 0.1, step: 0.1 }}
                helperText={isMetric ? "Enter depth in meters" : "Enter depth in feet"}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={locationSearchResults}
                getOptionLabel={(option) => typeof option === 'string' ? option : option.displayName}
                loading={locationSearchLoading}
                value={selectedLocation}
                inputValue={locationInputValue}
                onChange={(_, newValue) => {
                  if (typeof newValue === 'string') {
                    setFormData({ ...formData, location: newValue });
                    setSelectedLocation(null);
                  } else {
                    handleLocationSelect(newValue);
                    if (newValue) {
                      setLocationInputValue(newValue.displayName);
                    }
                  }
                }}
                onInputChange={(_, newInputValue, reason) => {
                  setLocationInputValue(newInputValue);
                  if (reason === 'input') {
                    setFormData({ ...formData, location: newInputValue });
                    handleLocationSearch(newInputValue);
                  }
                }}
                filterOptions={(x) => x} // Disable client-side filtering since we do server-side search
                noOptionsText={locationInputValue.length < 3 ? "Type at least 3 characters to search" : "No dive sites found"}
                loadingText="Searching dive sites..."
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Dive Site Location"
                    placeholder="Search for dive site (e.g., Key Largo, Great Barrier Reef)"
                    helperText={
                      locationSearchLoading
                        ? "Searching..."
                        : locationSearchResults.length > 0
                          ? `${locationSearchResults.length} results found`
                          : "Search for a location or type manually"
                    }
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <>
                          {locationSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={`${option.latitude}-${option.longitude}`}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', width: '100%', py: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                        <LocationOn color="action" sx={{ fontSize: '1.2rem' }} />
                        <Typography variant="body2" sx={{ fontWeight: 500, flexGrow: 1 }}>
                          {option.displayName.split(',')[0]}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem', ml: 4 }}>
                        {option.displayName.split(',').slice(1).join(',')}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, ml: 4 }}>
                        <Chip label={option.type} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        {option.address.water && (
                          <Chip label="Dive Site" size="small" color="primary" sx={{ height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Box>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            {selectedLocation && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    <strong>Coordinates:</strong> {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={gettingCurrentLocation ? <CircularProgress size={16} /> : <MyLocation />}
                  onClick={handleUseCurrentLocation}
                  disabled={gettingCurrentLocation}
                  sx={{ textTransform: 'none' }}
                >
                  {gettingCurrentLocation ? 'Getting location...' : 'Use my current location'}
                </Button>
                <Button
                  size="small"
                  startIcon={<MyLocation />}
                  onClick={() => setShowManualCoords(!showManualCoords)}
                  sx={{ textTransform: 'none' }}
                >
                  {showManualCoords ? 'Hide' : 'Enter'} coordinates manually
                </Button>
              </Box>
            </Grid>
            {showManualCoords && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Latitude"
                    type="number"
                    value={formData.latitude}
                    onChange={handleChange('latitude')}
                    onPaste={handleCoordinatePairPaste}
                    fullWidth
                    placeholder="e.g., -18.2871"
                    inputProps={{ step: 0.000001 }}
                    helperText={'GPS coordinate — paste a "lat, long" pair to fill both'}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Longitude"
                    type="number"
                    value={formData.longitude}
                    onChange={handleChange('longitude')}
                    onPaste={handleCoordinatePairPaste}
                    fullWidth
                    placeholder="e.g., 147.6992"
                    inputProps={{ step: 0.000001 }}
                    helperText="GPS coordinate"
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Dive Details (Optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={`Average Depth (${depthLabel})`}
                type="number"
                value={formData.avgDepth}
                onChange={handleChange('avgDepth')}
                fullWidth
                inputProps={{ min: 0, step: 0.1 }}
                helperText={isMetric ? "Enter depth in meters" : "Enter depth in feet"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={`Water Temperature (${tempLabel})`}
                type="number"
                value={formData.waterTemperature}
                onChange={handleChange('waterTemperature')}
                fullWidth
                inputProps={{ step: 0.1 }}
                helperText={isMetric ? "Enter temperature in Celsius" : "Enter temperature in Fahrenheit"}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Activity Type"
                value={formData.diveType}
                onChange={handleChange('diveType')}
                fullWidth
                helperText="Select the type of underwater activity"
              >
                {getAllActivityTypes().map((activityType) => (
                  <MenuItem key={activityType} value={activityType}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span style={{ fontSize: '1.2em' }}>{getActivityTypeIcon(activityType)}</span>
                      <span>{getActivityTypeDisplayName(activityType)}</span>
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Visibility"
                value={formData.visibility}
                onChange={handleChange('visibility')}
                fullWidth
              >
                <MenuItem value="Excellent">Excellent (&gt;30m)</MenuItem>
                <MenuItem value="Good">Good (15-30m)</MenuItem>
                <MenuItem value="Fair">Fair (5-15m)</MenuItem>
                <MenuItem value="Poor">Poor (&lt;5m)</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Safety Stop (minutes)"
                type="number"
                value={formData.safetyStopMinutes}
                onChange={handleChange('safetyStopMinutes')}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h6" gutterBottom>
            Equipment (Optional)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Weight Used"
                type="number"
                value={formData.weightUsed}
                onChange={handleChange('weightUsed')}
                fullWidth
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <TextField
                select
                label="Unit"
                value={formData.weightUnit}
                onChange={handleChange('weightUnit')}
                fullWidth
              >
                <MenuItem value="kg">kg</MenuItem>
                <MenuItem value="lbs">lbs</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Pressure Unit"
                value={formData.pressureUnit}
                onChange={handlePressureUnitChange}
                fullWidth
              >
                <MenuItem value="bar">bar</MenuItem>
                <MenuItem value="psi">psi</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Tank Size"
                value={formData.tankSize}
                onChange={handleChange('tankSize')}
                fullWidth
              >
                <MenuItem value="AL80">AL80 (11.0L)</MenuItem>
                <MenuItem value="AL63">AL63 (8.5L)</MenuItem>
                <MenuItem value="AL100">AL100 (13.2L)</MenuItem>
                <MenuItem value="Steel72">Steel 72 (9.6L)</MenuItem>
                <MenuItem value="Steel100">Steel 100 (12.0L)</MenuItem>
                <MenuItem value="Steel120">Steel 120 (15.1L)</MenuItem>
                <MenuItem value="Steel130">Steel 130 (16.4L)</MenuItem>
                <MenuItem value="custom">Custom</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Start Pressure"
                type="number"
                value={formData.startPressure}
                onChange={handleChange('startPressure')}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                helperText="Tank pressure before the dive"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="End Pressure"
                type="number"
                value={formData.endPressure}
                onChange={handleChange('endPressure')}
                fullWidth
                inputProps={{ min: 0, step: 1 }}
                helperText="Tank pressure after the dive"
              />
            </Grid>
            {formData.tankSize === 'custom' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Custom Tank Volume (L)"
                  type="number"
                  value={formData.customTankVolume}
                  onChange={handleChange('customTankVolume')}
                  fullWidth
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
            )}
            {(formData.startPressure && formData.endPressure) && (
              <Grid item xs={12}>
                <Alert severity={parseFloat(formData.startPressure) > parseFloat(formData.endPressure) ? 'info' : 'warning'} sx={{ py: 0.5 }}>
                  <Typography variant="caption">
                    Pressure used: {parseFloat(formData.startPressure) > parseFloat(formData.endPressure)
                      ? `${(parseFloat(formData.startPressure) - parseFloat(formData.endPressure)).toFixed(0)} ${formData.pressureUnit}`
                      : 'Start pressure should be greater than end pressure'}
                  </Typography>
                </Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Gas Consumption Metric"
                value={formData.gasMetric}
                onChange={handleGasMetricChange}
                fullWidth
                helperText={getGasConsumptionMetricTitle(formData.gasMetric)}
              >
                <MenuItem value="rmv">RMV</MenuItem>
                <MenuItem value="sac">SAC</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label={`${formData.gasMetric === 'sac' ? 'SAC' : 'RMV'} (${getGasConsumptionUnitLabel(formData.gasMetric, formData.pressureUnit as PressureUnit)})`}
                type="number"
                value={formData.gasConsumption}
                onChange={handleChange('gasConsumption')}
                fullWidth
                disabled={gasAutoCalculated}
                inputProps={{ min: 0, step: 0.1 }}
                helperText={
                  gasAutoCalculated
                    ? 'Calculated from tank pressures'
                    : formData.gasMetric === 'sac' && !formTankVolume
                      ? 'Select a tank size to enter SAC'
                      : 'Enter directly, or fill tank pressures to calculate'
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Suit Type"
                value={formData.suitType}
                onChange={handleChange('suitType')}
                fullWidth
              >
                <MenuItem value="none">No Suit</MenuItem>
                <MenuItem value="wetsuit">Wetsuit</MenuItem>
                <MenuItem value="drysuit">Drysuit</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Suit Thickness (mm)"
                type="number"
                value={formData.suitThicknessMm}
                onChange={handleChange('suitThicknessMm')}
                fullWidth
                disabled={formData.suitType === 'none'}
                inputProps={{ min: 0, step: 0.5 }}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Dive'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiveEntryForm;
